import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, Zap } from 'lucide-react'
import { MarketingShell } from './MarketingShell'
import { WAAllowanceMeter } from './WAAllowanceMeter'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import apiClient from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import { describeApiError } from '@/lib/apiError'

interface AutomationRow {
  key: string
  enabled: boolean
  channel: 'email' | 'whatsapp_api'
  wa_template_name?: string
  wa_template_params: string[]
  email_subject: string
  email_body: string
  config: Record<string, unknown>
  sent_last_30d: number
}

const AUTOMATION_COPY: Record<string, { title: string; blurb: string }> = {
  welcome: {
    title: 'Welcome new members',
    blurb: 'One message when a guest joins the loyalty program — sent once, ever.',
  },
  birthday: {
    title: 'Birthday greetings',
    blurb: 'Runs daily at 9:00 — members with a birthday this month, once per 30 days.',
  },
  winback: {
    title: 'Win back lapsed guests',
    blurb: 'Regulars who haven’t visited in 60+ days get a nudge, once per 30 days.',
  },
  one_stamp_away: {
    title: 'One stamp away',
    blurb: 'Fires the moment a guest lands one visit short of their reward.',
  },
  review_request: {
    title: 'Review requests',
    blurb: 'Ask for a review after a visit. Off by default — uses WhatsApp sends fastest.',
  },
  referral_reward: {
    title: 'Referral rewards',
    blurb: 'Notifies both guests when a referral earns them each a stamp.',
  },
}

/**
 * Autopilot automations. The rows are seeded server-side (default-ON per
 * product decision), so this page is toggle + channel choice — the rules
 * run without the owner ever opening it, which is exactly the point.
 */
export function AdminAutomations() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [saving, setSaving] = useState<string | null>(null)

  const listQuery = useQuery({
    queryKey: ['marketing', 'automations'],
    queryFn: () => apiClient.listAutomations(),
    staleTime: 30_000,
  })
  const automations: AutomationRow[] = listQuery.data?.data?.automations ?? []

  const update = useMutation({
    mutationFn: ({ key, body }: { key: string; body: Record<string, unknown> }) =>
      apiClient.updateAutomation(key, body),
    onMutate: ({ key }) => setSaving(key),
    onSettled: () => setSaving(null),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing', 'automations'] })
    },
    onError: (err: unknown) => {
      toast({
        title: 'Could not save',
        description: describeApiError(err).message,
        variant: 'destructive',
      })
    },
  })

  return (
    <MarketingShell
      title="Automations"
      description="Always-on messages that run themselves — welcomes, birthdays, win-backs and reward nudges, with no campaign to remember."
    >
      <div className="space-y-4">
        <WAAllowanceMeter />

        {listQuery.isLoading ? (
          <div className="flex h-40 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <ul className="space-y-2">
            {automations.map((a) => {
              const copy = AUTOMATION_COPY[a.key] ?? { title: a.key, blurb: '' }
              return (
                <li
                  key={a.key}
                  className="rounded-lg border border-border/80 bg-background p-4 shadow-sm"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <Zap
                          className={`h-4 w-4 ${a.enabled ? 'text-amber-500' : 'text-muted-foreground'}`}
                          aria-hidden
                        />
                        <h3 className="font-semibold">{copy.title}</h3>
                        {a.sent_last_30d > 0 && (
                          <Badge variant="outline" className="text-xs">
                            {a.sent_last_30d} sent · 30d
                          </Badge>
                        )}
                      </div>
                      <p className="mt-0.5 max-w-xl text-sm text-muted-foreground">
                        {copy.blurb}
                      </p>
                      {a.channel === 'whatsapp_api' && a.wa_template_name && (
                        <p className="mt-1 font-mono text-xs text-muted-foreground">
                          template: {a.wa_template_name}
                        </p>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      <Select
                        value={a.channel}
                        onValueChange={(v) =>
                          update.mutate({ key: a.key, body: { channel: v } })
                        }
                        disabled={saving === a.key}
                      >
                        <SelectTrigger className="h-8 w-[9.5rem] text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="whatsapp_api">WhatsApp</SelectItem>
                          <SelectItem value="email">Email</SelectItem>
                        </SelectContent>
                      </Select>
                      {saving === a.key ? (
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      ) : (
                        <Switch
                          checked={a.enabled}
                          onCheckedChange={(v) =>
                            update.mutate({ key: a.key, body: { enabled: v } })
                          }
                        />
                      )}
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        )}

        <DigestSettingsCard />

        <p className="text-xs text-muted-foreground">
          WhatsApp automations only reach guests who opted in, and pause
          automatically when the monthly allowance runs out. Email automations
          need the sending domain configured under Settings.
        </p>
      </div>
    </MarketingShell>
  )
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

/**
 * Weekly owner digest — "your week: 34 new members, 210 visits, 12 going
 * quiet", delivered to the OWNER's WhatsApp (or email fallback). Lives on
 * the Automations tab because it's conceptually one more always-on rule.
 */
function DigestSettingsCard() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [draft, setDraft] = useState<{
    enabled: boolean
    owner_phone: string
    owner_email: string
    weekday: number
    hour: number
  } | null>(null)

  const settingsQuery = useQuery({
    queryKey: ['marketing', 'digest-settings'],
    queryFn: () => apiClient.getDigestSettings(),
    staleTime: 60_000,
  })
  const saved = settingsQuery.data?.data
  const current = draft ?? saved

  const save = useMutation({
    mutationFn: (body: NonNullable<typeof draft>) => apiClient.updateDigestSettings(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing', 'digest-settings'] })
      setDraft(null)
      toast({ title: 'Digest settings saved' })
    },
    onError: (err: unknown) => {
      toast({
        title: 'Could not save',
        description: describeApiError(err).message,
        variant: 'destructive',
      })
    },
  })

  if (!current) return null

  const set = (patch: Partial<NonNullable<typeof draft>>) =>
    setDraft({ ...current, ...patch })

  return (
    <div className="rounded-lg border border-border/80 bg-background p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold">Weekly owner digest</h3>
          <p className="mt-0.5 max-w-xl text-sm text-muted-foreground">
            One message a week to you: new members, visits, and regulars going
            quiet — so the numbers find you, not the other way round.
          </p>
        </div>
        <Switch checked={current.enabled} onCheckedChange={(v) => set({ enabled: v })} />
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">
            Owner WhatsApp number
          </label>
          <input
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
            value={current.owner_phone}
            onChange={(e) => set({ owner_phone: e.target.value })}
            placeholder="0300 1234567"
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">
            Fallback email
          </label>
          <input
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
            value={current.owner_email}
            onChange={(e) => set({ owner_email: e.target.value })}
            placeholder="owner@example.com"
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Day</label>
          <Select
            value={String(current.weekday)}
            onValueChange={(v) => set({ weekday: Number(v) })}
          >
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WEEKDAYS.map((d, i) => (
                <SelectItem key={d} value={String(i)}>
                  {d}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Hour</label>
          <Select
            value={String(current.hour)}
            onValueChange={(v) => set({ hour: Number(v) })}
          >
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Array.from({ length: 24 }, (_, h) => (
                <SelectItem key={h} value={String(h)}>
                  {String(h).padStart(2, '0')}:00
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {draft && (
        <div className="mt-3 flex justify-end gap-2">
          <button
            className="rounded-md border px-3 py-1.5 text-sm"
            onClick={() => setDraft(null)}
            disabled={save.isPending}
          >
            Cancel
          </button>
          <button
            className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground"
            onClick={() => save.mutate(draft)}
            disabled={save.isPending}
          >
            {save.isPending ? 'Saving…' : 'Save digest settings'}
          </button>
        </div>
      )}
    </div>
  )
}
